import { Component, EventEmitter, Input, OnInit, Output, QueryList, SimpleChanges, ViewChildren } from '@angular/core';
import { ServiceDeliveries } from '../../model/service-deliveries.model';
import { ServiceDeliveryService } from '../../service-delivery/service/service-delivery.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Subscription } from 'rxjs';
import { AccessService } from 'src/app/component/navigation/service/access.service';
import { Region } from 'src/app/model/region.model';
import { Student } from 'src/app/model/student/student.model';
import { CleanDatePipe } from 'src/app/pipes/clean-date.pipe';
import { GetPropertyPipe } from 'src/app/pipes/get-property.pipe';
import { SimpleDatePipe } from 'src/app/pipes/simple-date.pipe';
import { HelperService } from 'src/app/service/helper.service';
import { LoginTokenService } from 'src/app/service/login-token.service';
import { StudentHelperService } from '../../service/student-helper.service';
import { EditChildServiceParticipationModalComponent } from './edit-child-service-participation-modal/edit-child-service-participation-modal.component';

@Component({
    selector: 'edit-child-service-participation',
    templateUrl: './edit-child-service-participation.component.html',
    styleUrls: ['./edit-child-service-participation.component.scss']
})
export class EditChildServiceParticipationComponent implements OnInit {
    @Input() studentId!: number;
    @Input() editing: boolean;
    @Output() editingChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    student: Student;
    serviceDeliveries: ServiceDeliveries[] = [];
    userRegion: Region;
    userSchoolDistricts: any;
    missingCOE: boolean;

    featuresCheck: string[] = ['Edit Service Deliveries'];
    featurePermissions: any = {};
    hasAdminRole: boolean = false;

    isLoading: boolean = false;
    promises: Array<any> = [];

    getPropertyPipe = new GetPropertyPipe();
    cleanDatePipe = new CleanDatePipe();
    simpleDatePipe = new SimpleDatePipe();

    // Table display
    serviceDeliveryDisplayedColumns: string[] = [
        'EDIT',
        'SCHOOL_YEAR',
        'ENROLLMENT_PERIODS.PERIOD',
        'GRADES',
        'SCHOOL_DISTRICTS.DISTRICTS',
        'SCHOOL.NAME',
        'SERVICE_NAME',
        'SERVICE_CODE',
        'START_DATE',
        'END_DATE',
        'COS_REASONS.REASON',
        'SERVICE_HOUR_RANGES.MIN',
        'CREATED_DATE'
    ];

    // Table results
    @ViewChildren(MatSort) sorts: QueryList<MatSort> = new QueryList<MatSort>();
    regions: any[] = [];
    dataSource: MatTableDataSource<ServiceDeliveries>;

    subs: Subscription[] = [];

    constructor(
        private serviceDeliveryService: ServiceDeliveryService,
        private helperService: HelperService,
        private dialog: MatDialog,
        private studentHelper: StudentHelperService,
        private accessService: AccessService,
        private loginTokenService: LoginTokenService
    ) {
        this.subscriptions();
    }

    subscriptions() {
        let studentSub = this.studentHelper.student$.subscribe((student) => {
            this.student = student;
        });

        this.subs.push(studentSub);
    }

    ngOnInit(): void {}

    init() {
        this.isLoading = true;

        this.getServiceDeliveries();
        this.getUserRegion();
        this.getUserSchoolDistricts();
        this.getFeaturePermissions();
        this.getAdministrator();
        Promise.all(this.promises).then((res) => {
            this.updateDataSource();

            this.isLoading = false;
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (this.editing) {
            this.serviceDeliveryDisplayedColumns = [
                'EDIT',
                'SCHOOL_YEAR',
                'ENROLLMENT_PERIODS.PERIOD',
                'GRADES',
                'SCHOOL_DISTRICTS.DISTRICTS',
                'SCHOOL.NAME',
                'SERVICE_NAME',
                'SERVICE_CODE',
                'START_DATE',
                'END_DATE',
                'COS_REASONS.REASON',
                'SERVICE_HOUR_RANGES.MIN',
                'CREATED_DATE'
            ];
        } else {
            this.serviceDeliveryDisplayedColumns = [
                'EDIT',
                'SCHOOL_YEAR',
                'ENROLLMENT_PERIODS.PERIOD',
                'GRADES',
                'SCHOOL_DISTRICTS.DISTRICTS',
                'SCHOOL.NAME',
                'SERVICE_NAME',
                'SERVICE_CODE',
                'START_DATE',
                'END_DATE',
                'COS_REASONS.REASON',
                'SERVICE_HOUR_RANGES.MIN',
                'CREATED_DATE'
            ];
            if (changes['editing']) {
                this.init();
            }
        }
    }

    getUserRegion() {
        let deferred = new Promise((resolve, reject) => {
            this.helperService.lstUserProfileRegion$.subscribe((userRegion: Region[]) => {
                this.userRegion = userRegion[0];

                resolve(this.userRegion);
            });
        });

        this.promises.push(deferred);
    }

    getUserSchoolDistricts() {
        let deferred = new Promise((resolve, reject) => {
            this.accessService.getSchoolDistrictAccess().subscribe((schoolDistricts) => {
                if (schoolDistricts) {
                    this.userSchoolDistricts = schoolDistricts;
                }
                resolve(schoolDistricts);
            });
        });

        this.promises.push(deferred);
    }

    getServiceDeliveries() {
        let deferred = new Promise((resolve, reject) => {
            this.serviceDeliveryService.getStudentServices(this.studentId).subscribe((res) => {
                if (res.result) {
                    this.serviceDeliveries = JSON.parse(res.result);

                    resolve(res);
                } else if (res?.msg) {
                    resolve(res);
                } else {
                    reject('err');
                }
            });
        });

        this.promises.push(deferred);
    }

    getFeaturePermissions() {
        let deferred = new Promise((resolve, reject) => {
            this.accessService.getFeatureAccessPromise(this.featuresCheck).then((res) => {
                this.featurePermissions = res;
                resolve(res);
            });
        });
        this.promises.push(deferred);
    }

    getAdministrator() {
        let deferred = new Promise((resolve, reject) => {
            let user = this.loginTokenService.getUser();
            let adminUserIds = [1, 9]; // System Administrator (1), State Administrator (9)
            this.hasAdminRole = user.USER_ROLES?.some((ur) => adminUserIds.includes(ur.ROLE?.ID ? ur.ROLE.ID : 0))!;
            resolve(this.hasAdminRole);
        });
        this.promises.push(deferred);
    }

    updateDataSource() {
        setTimeout(() => {
            this.regions = this.getRegions(this.serviceDeliveries);

            setTimeout(() => {
                this.regions.forEach((region, index) => {
                    let dataSource = new MatTableDataSource<ServiceDeliveries>();
                    dataSource.data = region.SERVICE_DELIVERIES.sort((a: any, b: any) => (a.START_DATE! > b.START_DATE! ? -1 : 1));

                    dataSource.sort = this.sorts.toArray()[index];
                    const defaultSort: Sort = { active: 'SCHOOL_YEAR', direction: 'desc' };
                    dataSource.sort.active = defaultSort.active;
                    dataSource.sort.direction = defaultSort.direction;
                    dataSource.sortingDataAccessor = (obj, property) => this.getPropertyPipe.transform(obj, property, true);

                    region.tableDataSource = dataSource;

                    setTimeout(() => {
                        region.tableDataSource.sort?.sortChange.emit(defaultSort);
                    });
                });
            });
        });
    }

    getRegions(serviceDeliveries: ServiceDeliveries[]) {
        let regions: any[] = [];

        serviceDeliveries.forEach((se) => {
            let regionIndex = regions.findIndex((r) => r.CODE === se.REGION.CODE);

            if (regionIndex === -1) {
                let region: any = se.REGION;
                region.SERVICE_DELIVERIES = [];
                region.SERVICE_DELIVERIES.push(se);

                regions.push(region);
            } else {
                regions[regionIndex].SERVICE_DELIVERIES.push(se);
            }
        });

        // Set user's region to top
        if (this.userRegion) {
            regions = regions.sort((a, b) => (a.CODE! > b.CODE! ? 1 : -1));
            let userRegionIndex = regions.findIndex((r) => r.CODE === this.userRegion.CODE);

            if (userRegionIndex >= 0) {
                regions.splice(0, 0, regions.splice(userRegionIndex, 1)[0]);
            }
        } else {
            regions = regions.sort((a, b) => (a.CODE! > b.CODE! ? 1 : -1));
        }

        return regions;
    }

    getCreatedOn(createdDate: any) {
        return 'Created on ' + this.simpleDatePipe.transform(this.cleanDatePipe.transform(createdDate));
    }

    editDialog(serviceDelivery?: ServiceDeliveries) {
        let payload: any = {};
        payload.students = [{ ID: this.studentId, DOB: this.student.DOB, MSD_NUMBER: this.student.MSD_NUMBER, PERSON: this.student.PERSON }];
        if (serviceDelivery) {
            payload.serviceDelivery = serviceDelivery;
        }
        const dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = true;
        dialogConfig.data = payload;
        dialogConfig.maxHeight = '99vh';
        dialogConfig.minWidth = '80vw';
        const dialogRef = this.dialog.open(EditChildServiceParticipationModalComponent, dialogConfig);
        dialogRef.afterClosed().subscribe((res: any) => {
            if (res) {
                if (res.refresh) {
                    this.init();
                }
            }
        });
    }

    editDialogCos(serviceDelivery?: ServiceDeliveries) {
        let payload: any = {};
        payload.students = [{ ID: this.studentId, DOB: this.student.DOB, MSD_NUMBER: this.student.MSD_NUMBER, PERSON: this.student.PERSON }];
        if (serviceDelivery) {
            payload.serviceDelivery = serviceDelivery;
        }
        payload.cosMode = true;
        const dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = true;
        dialogConfig.data = payload;
        dialogConfig.maxHeight = '99vh';
        dialogConfig.minWidth = '80vw';
        const dialogRef = this.dialog.open(EditChildServiceParticipationModalComponent, dialogConfig);
        dialogRef.afterClosed().subscribe((res: any) => {
            if (res) {
                if (res.refresh) {
                    this.init();
                }
            }
        });
    }

    viewDialog(serviceDelivery?: ServiceDeliveries) {
        let payload: any = {};
        payload.students = [{ ID: this.studentId }];
        if (serviceDelivery) {
            payload.serviceDelivery = serviceDelivery;
            if (serviceDelivery.COS_REASONS?.REASON) {
                payload.cosMode = true;
            }
        }
        payload.mode = 'view';
        const dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = false;
        dialogConfig.data = payload;
        dialogConfig.maxHeight = '99vh';
        dialogConfig.minWidth = '80vw';
        const dialogRef = this.dialog.open(EditChildServiceParticipationModalComponent, dialogConfig);
        dialogRef.afterClosed().subscribe((res: any) => {
            if (res) {
                console.log(res.msg);
            }
        });
    }

    deleteServiceDelivery(serviceDelivery: ServiceDeliveries) {
        if (serviceDelivery && serviceDelivery.ID) {
            this.serviceDeliveryService.deleteByID(serviceDelivery.ID).subscribe({
                next: (res) => {
                    if (res.msg) {
                        if (res.msg.SUCCESS) {
                            let findServiceDeliveriesIndex = this.serviceDeliveries.findIndex((se: ServiceDeliveries) => se.ID === serviceDelivery.ID);

                            if (findServiceDeliveriesIndex >= 0) {
                                this.serviceDeliveries.splice(findServiceDeliveriesIndex, 1);

                                this.updateDataSource();
                            }
                        } else {
                        }
                    } else {
                    }
                },
                error: (err) => {},
                complete: () => {}
            });
        } else {
        }
    }
}
